import murmur from 'murmurhash-js'

import { dataLayerPush } from 'client/contexts/Tracking'
import { trackingRequest as kafkaSend } from 'client/contexts/Tracking/requests'
import { getSessionId } from 'client/contexts/Tracking/utils'
import { MISSING_EXPERIMENT } from 'src/context/experiment/constants'
import type { Experiment } from 'src/types/experiment'

import type { ReportExperimentType } from './types'
import { DEFAULT_AFFILIATE_ID } from 'src/pages/_middleware/defaultAffiliate'
import type { AffiliateType } from '@s4l/stayforlong/shared/domain/Affiliate'
import { Affiliate } from '@s4l/stayforlong/shared/domain/Affiliate'

export function validateExperiment(
  experiment: Experiment | typeof MISSING_EXPERIMENT | null,
  experimentKey: string
) {
  if (!experiment) {
    return false
  }

  if (experiment === MISSING_EXPERIMENT) {
    return false
  }

  if (experiment.key !== experimentKey) {
    return false
  }

  return experiment
}

export function reportToKafka(kafkaEventPayload: ReportExperimentType) {
  const {
    affiliate = DEFAULT_AFFILIATE_ID,
    referrer,
    experiment,
    market,
    language,
    device,
    ip,
    isBot,
    pageId,
    isLogged,
    customer,
    mobileAppVersion,
  } = kafkaEventPayload
  const eventName = 'experiment'
  const eventType = 'experiment'

  if (!experiment || experiment === MISSING_EXPERIMENT) return

  const timestamp = Date.now()

  const eventPayload = {
    hsl: murmur(`${eventName}${timestamp}${eventType}`, 87) || null,
    level: 'info',
    event_sub_type: null,
    ip,
    is_bot: isBot,
    created_at: timestamp,
    screen: `${window.screen.width}x${window.screen.height}`,
    session_id: getSessionId(),
    language: language || null,
    source: 'web',
    market,
    event_type: eventType || null,
    user_id: customer?.userId || null,
    scope: 'tracking',
    event_name: eventName,
    device,
    user_agent: navigator.userAgent,
    platform: mobileAppVersion ? 'app' : 'web',
    payload: {
      affiliate: Affiliate.create({ value: affiliate as AffiliateType }).toEvent({
        origin: referrer,
      }),
      page: pageId,
      experiments: [{ experiment_id: experiment.key, experiment_variant: experiment.variant }],
      is_logged: isLogged,
      origin: referrer,
    },
  }

  kafkaSend(eventPayload)
}

export function reportToDataLayer(dataLayerEventPayload: ReportExperimentType) {
  const { experiment } = dataLayerEventPayload
  const payload = []
  if (experiment && experiment !== MISSING_EXPERIMENT) {
    payload.push({
      event: 'sflExperiment',
      data: {
        expId: experiment.id,
        expVar: experiment.variant,
      },
    })
  }

  dataLayerPush(payload)
}

export function reportExperiment(experimentPayload: ReportExperimentType) {
  reportToDataLayer(experimentPayload)
  reportToKafka(experimentPayload)
}
